import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import axios, { CancelTokenSource } from 'axios/index';
import { debounce } from 'lodash';

import { CancelReasons } from '~src/services/ApiController';

interface Props {
  onChange: (event: React.ChangeEvent<HTMLInputElement>, source: CancelTokenSource) => Promise<void>;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  debounceTimeout: number;
  setInputNode?: (node: HTMLInputElement | null) => void;
  className?: string;
  placeholder?: { key: string; value: string };
  minLength?: number;
}

const DebounceInput: FC<Props> = props => {
  const { CancelToken } = axios;
  const [source, setSource] = useState(CancelToken.source());
  const [pendingRequest, setPendingRequest] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const debounceFunc = useCallback(
    debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      if (pendingRequest) {
        source.cancel(CancelReasons.DEBOUNCE);
        setSource(CancelToken.source());
      }
      setPendingRequest(true);
      const onChangePromise = props.onChange(event, source);
      if (onChangePromise) {
        onChangePromise.then(() => {
          setPendingRequest(false);
        });
      }
    }, props.debounceTimeout),
    [],
  );

  useEffect(() => {
    if (inputRef.current && props.setInputNode) {
      props.setInputNode(inputRef.current);
    }
    return () => {
      debounceFunc.flush();
    };
  }, []);

  const debounceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    if (!props.minLength || event.target.value.length >= props.minLength) {
      debounceFunc(event);
    }
  };

  return (
    <input
      className={props.className}
      ref={inputRef}
      onChange={debounceChange}
      placeholder={props.placeholder ? props.placeholder.value : undefined}
      data-translation={props.placeholder ? props.placeholder.key : undefined}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
    />
  );
};

export default DebounceInput;
